import React, { useState, useEffect } from 'react';
import { Video, Play, Loader, CheckCircle, AlertCircle, Copy, Link, Share2, Download } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface TavusVideoIntegrationProps {
  isOpen: boolean;
  onClose: () => void;
  apiKey?: string;
}

interface VideoTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  thumbnailUrl: string;
}

interface VideoGeneration {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  videoUrl?: string;
  error?: string;
  createdAt: string;
}

export function TavusVideoIntegration({ isOpen, onClose, apiKey = 'c415e23a929f4c4d811066c32ee4ed0a' }: TavusVideoIntegrationProps) {
  const [activeTab, setActiveTab] = useState<'create' | 'library'>('create');
  const [templates, setTemplates] = useState<VideoTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<VideoTemplate | null>(null);
  const [scriptContent, setScriptContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [generatedVideos, setGeneratedVideos] = useState<VideoGeneration[]>([]);
  const [currentVideo, setCurrentVideo] = useState<VideoGeneration | null>(null);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  // Fetch templates and generated videos on mount
  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
      fetchGeneratedVideos();
    }
  }, [isOpen]);

  const fetchTemplates = async () => {
    try {
      // In a real implementation, this would fetch from Tavus API
      // For demo purposes, we'll use mock data
      const mockTemplates: VideoTemplate[] = [
        {
          id: 'template-1',
          name: 'Platform Introduction',
          description: 'Introduce users to the Agent Store platform and its key features',
          category: 'Onboarding',
          thumbnailUrl: 'https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
        },
        {
          id: 'template-2',
          name: 'Agent Explanation',
          description: 'Explain how a specific AI agent works and its capabilities',
          category: 'Product',
          thumbnailUrl: 'https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
        },
        {
          id: 'template-3',
          name: 'A2A Protocol Guide',
          description: 'Guide users through the Agent-to-Agent protocol implementation',
          category: 'Technical',
          thumbnailUrl: 'https://images.pexels.com/photos/2599244/pexels-photo-2599244.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
        },
        {
          id: 'template-4',
          name: 'Deployment Tutorial',
          description: 'Step-by-step guide for deploying agents to production',
          category: 'Tutorial',
          thumbnailUrl: 'https://images.pexels.com/photos/3861958/pexels-photo-3861958.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
        }
      ];
      
      setTemplates(mockTemplates);
      if (!selectedTemplate && mockTemplates.length > 0) {
        setSelectedTemplate(mockTemplates[0]);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchGeneratedVideos = async () => {
    try {
      // In a real implementation, this would fetch from your database
      // For demo purposes, we'll use mock data
      const mockVideos: VideoGeneration[] = [
        {
          id: 'video-1',
          status: 'completed',
          videoUrl: 'https://player.vimeo.com/video/684312486',
          createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 'video-2',
          status: 'completed',
          videoUrl: 'https://player.vimeo.com/video/684312486',
          createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString()
        }
      ];
      
      setGeneratedVideos(mockVideos);
    } catch (error) {
      console.error('Error fetching generated videos:', error);
    }
  };

  const generateVideo = async () => {
    if (!selectedTemplate || !scriptContent.trim()) {
      setGenerationStatus('error');
      setStatusMessage('Please select a template and provide script content');
      return;
    }

    setIsGenerating(true);
    setGenerationStatus('idle');
    setStatusMessage('');

    try {
      // In a real implementation, this would call the Tavus API
      // For demo purposes, we'll simulate the API call
      console.log('Generating video with Tavus API key:', apiKey);
      console.log('Template:', selectedTemplate.id);
      console.log('Script:', scriptContent);
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate successful response
      const newVideo: VideoGeneration = {
        id: `video-${Date.now()}`,
        status: 'completed',
        videoUrl: 'https://player.vimeo.com/video/684312486', // Example video
        createdAt: new Date().toISOString()
      };
      
      setGeneratedVideos(prev => [newVideo, ...prev]);
      setCurrentVideo(newVideo);
      setGenerationStatus('success');
      setStatusMessage('Video generated successfully!');
      
      // Track the video generation in analytics
      try {
        await supabase.from('analytics_events').insert({
          event_type: 'video_generation',
          session_id: 'tavus_session',
          metadata: {
            template_id: selectedTemplate.id,
            template_name: selectedTemplate.name,
            script_length: scriptContent.length,
            video_id: newVideo.id
          }
        });
      } catch (error) {
        console.error('Error tracking video generation:', error);
      }
      
    } catch (error) {
      console.error('Error generating video:', error);
      setGenerationStatus('error');
      setStatusMessage(error instanceof Error ? error.message : 'Failed to generate video');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(type);
    setTimeout(() => setCopySuccess(null), 2000);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Ensure all video URLs use HTTPS
  const ensureHttps = (url: string | undefined): string => {
    if (!url) return '';
    return url.replace(/^http:\/\//i, 'https://');
  };

  // Sample script templates
  const scriptTemplates = {
    platform: "Hi there! I'm excited to show you Agent Store, your ultimate hub for discovering, deploying, and monetizing AI agents. Our platform brings together thousands of AI agents from GitHub, Hugging Face, and other sources, making it easy to find the perfect solution for your needs. Let me walk you through some key features...",
    agent: "Today I want to introduce you to one of our most powerful AI agents. This agent can help you with [specific task] and is perfect for [use case]. It's built using [technology] and has features like [key features]. Let me show you how to set it up and get the most out of it...",
    a2a: "The Agent-to-Agent protocol is a game-changer for building complex AI systems. It allows different AI agents to communicate seamlessly, creating powerful multi-agent workflows. In this video, I'll explain how A2A works and show you how to implement it in your own projects...",
    deployment: "Deploying AI agents can be challenging, but with Agent Store, it's simple. In this video, I'll walk you through our streamlined deployment process, from configuration to going live. You'll learn how to set up your environment, manage API keys, and monitor your deployed agents..."
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <div className="flex items-center gap-3">
            <Video className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-semibold">Tavus Video Creator</h2>
              <p className="text-sm opacity-90">Create personalized AI videos for your agents</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('create')}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'create'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Video className="w-4 h-4" />
              Create Video
            </button>
            <button
              onClick={() => setActiveTab('library')}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'library'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Play className="w-4 h-4" />
              Video Library
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'create' && (
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Templates */}
                <div className="lg:col-span-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Template</h3>
                  <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                    {templates.map((template) => (
                      <div
                        key={template.id}
                        onClick={() => setSelectedTemplate(template)}
                        className={`border rounded-xl overflow-hidden cursor-pointer transition-all ${
                          selectedTemplate?.id === template.id
                            ? 'border-blue-500 ring-2 ring-blue-200'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="h-32 bg-gray-100 overflow-hidden">
                          <img 
                            src={template.thumbnailUrl} 
                            alt={template.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="p-3">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="font-medium text-gray-900">{template.name}</h4>
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                              {template.category}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600">{template.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6">
                    <h4 className="font-medium text-gray-900 mb-2">Script Templates</h4>
                    <div className="space-y-2">
                      <button
                        onClick={() => setScriptContent(scriptTemplates.platform)}
                        className="w-full text-left px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        Platform Introduction
                      </button>
                      <button
                        onClick={() => setScriptContent(scriptTemplates.agent)}
                        className="w-full text-left px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        Agent Explanation
                      </button>
                      <button
                        onClick={() => setScriptContent(scriptTemplates.a2a)}
                        className="w-full text-left px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        A2A Protocol Guide
                      </button>
                      <button
                        onClick={() => setScriptContent(scriptTemplates.deployment)}
                        className="w-full text-left px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        Deployment Tutorial
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right Column - Script Editor and Preview */}
                <div className="lg:col-span-2">
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Video Script</h3>
                      <div className="text-sm text-gray-500">
                        {scriptContent.length} characters
                      </div>
                    </div>
                    <textarea
                      value={scriptContent}
                      onChange={(e) => setScriptContent(e.target.value)}
                      placeholder="Enter your video script here..."
                      rows={10}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <div className="mt-2 text-xs text-gray-500">
                      Recommended: 100-300 words for optimal video length
                    </div>
                  </div>

                  {/* Status Messages */}
                  {generationStatus === 'success' && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-5 h-5 flex-shrink-0" />
                      <span>{statusMessage}</span>
                    </div>
                  )}

                  {generationStatus === 'error' && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      <span>{statusMessage}</span>
                    </div>
                  )}

                  {/* Preview */}
                  {currentVideo && currentVideo.status === 'completed' && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
                      <div className="bg-black rounded-lg overflow-hidden aspect-video">
                        <iframe
                          src={ensureHttps(currentVideo.videoUrl)}
                          width="100%"
                          height="100%"
                          frameBorder="0"
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                          title="Generated video"
                        ></iframe>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2">
                        <button
                          onClick={() => copyToClipboard(ensureHttps(currentVideo.videoUrl || ''), 'link')}
                          className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                        >
                          {copySuccess === 'link' ? (
                            <>
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Link className="w-4 h-4" />
                              Copy Link
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => copyToClipboard(`<iframe src="${ensureHttps(currentVideo.videoUrl || '')}" width="640" height="360" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`, 'embed')}
                          className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                        >
                          {copySuccess === 'embed' ? (
                            <>
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Code className="w-4 h-4" />
                              Copy Embed Code
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => window.open(ensureHttps(currentVideo.videoUrl || ''), '_blank')}
                          className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                        >
                          <Play className="w-4 h-4" />
                          Open in New Tab
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Generate Button */}
                  <button
                    onClick={generateVideo}
                    disabled={isGenerating || !scriptContent.trim() || !selectedTemplate}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isGenerating ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        Generating Video...
                      </>
                    ) : (
                      <>
                        <Video className="w-5 h-5" />
                        Generate Video
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'library' && (
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Generated Videos</h3>
              
              {generatedVideos.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Video className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">No videos yet</h4>
                  <p className="text-gray-600 mb-6">
                    You haven't generated any videos yet. Create your first video to see it here.
                  </p>
                  <button
                    onClick={() => setActiveTab('create')}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Create Your First Video
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {generatedVideos.map((video) => (
                    <div key={video.id} className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="aspect-video bg-black relative">
                        {video.status === 'completed' ? (
                          <iframe
                            src={ensureHttps(video.videoUrl)}
                            width="100%"
                            height="100%"
                            frameBorder="0"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                            title="Generated video"
                          ></iframe>
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Loader className="w-8 h-8 text-white animate-spin" />
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-900">Video {video.id.split('-')[1]}</h4>
                          <span className="text-xs text-gray-500">{formatDate(video.createdAt)}</span>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          <button
                            onClick={() => {
                              setCurrentVideo(video);
                              setActiveTab('create');
                            }}
                            className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs"
                          >
                            <Play className="w-3 h-3" />
                            View
                          </button>
                          <button
                            onClick={() => copyToClipboard(ensureHttps(video.videoUrl || ''), `link-${video.id}`)}
                            className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                          >
                            {copySuccess === `link-${video.id}` ? (
                              <>
                                <CheckCircle className="w-3 h-3 text-green-500" />
                                Copied
                              </>
                            ) : (
                              <>
                                <Link className="w-3 h-3" />
                                Copy Link
                              </>
                            )}
                          </button>
                          <button
                            className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                          >
                            <Share2 className="w-3 h-3" />
                            Share
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Code(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </svg>
  );
}